
import { useMemo } from 'react';
import { useEntityCalculations } from '@/hooks/useEntityCalculations';
import { OrderFormData } from './types';

interface UseOrderCalculationsProps {
  formData?: OrderFormData;
  selectedStoreItems: Array<{ id: string; quantity: number }>;
  selectedKits: string[];
  discount?: number;
  // inventory items come from useInventoryUnified -> mapDbInventoryItemToInventoryItem
  // and already include dailyPrice/hourlyPrice in camelCase
  // We keep legacy fields optional for backward compatibility
  inventoryItems?: Array<{ id: string; name: string; dailyPrice?: number | null; hourlyPrice?: number | null; price?: number; price_per_day?: number }>;
  storeItems?: Array<{ id: string; name: string; price?: number }>;
  kits?: Array<{ id: string; name: string; price?: number }>;
}

export const useOrderCalculations = ({
  formData,
  selectedStoreItems,
  selectedKits,
  discount = 0,
  inventoryItems = [],
  storeItems = [],
  kits = []
}: UseOrderCalculationsProps) => {
  
  // ФИКС: Проверка готовности данных для расчёта (предотвращает render storms)
  const isReadyForCalculation = useMemo(() => {
    const hasSelectedItems = 
      (formData?.inventoryItems?.length ?? 0) > 0 ||
      selectedStoreItems.length > 0 ||
      selectedKits.length > 0;
    
    // Для расчёта нужны выбранные товары И (даты ИЛИ это покупка магазина)
    const hasDates = !!(formData?.startDate && formData?.endDate);
    const isStorePurchaseOnly = selectedStoreItems.length > 0 && (formData?.inventoryItems?.length ?? 0) === 0;
    
    return hasSelectedItems && (hasDates || isStorePurchaseOnly);
  }, [formData?.inventoryItems?.length, formData?.startDate, formData?.endDate, selectedStoreItems.length, selectedKits.length]);
  
  // Преобразуем данные в формат useEntityCalculations (мемоизировано)
  const entityInventoryItems = useMemo(() => {
    // Не обрабатываем если не готовы к расчёту
    if (!isReadyForCalculation && inventoryItems.length === 0) return [];
    
    return inventoryItems.map(item => {
      // Поддерживаем разные варианты полей цен: предпочитаем явные dailyPrice/hourlyPrice
      const explicitDaily = item.dailyPrice ?? undefined;
      const explicitHourly = item.hourlyPrice ?? undefined;
      const legacyDaily = item.price_per_day ?? item.price ?? undefined;
      const dailyPrice = (explicitDaily ?? legacyDaily ?? 0) as number;
      const hourlyPrice = (explicitHourly ?? (dailyPrice > 0 ? dailyPrice / 24 : 0)) as number;
      
      return {
        id: item.id,
        name: item.name,
        dailyPrice,
        hourlyPrice,
        price: item.price,
        price_per_day: item.price_per_day
      };
    });
  }, [inventoryItems, isReadyForCalculation]);

  const entityStoreItems = useMemo(() => {
    if (!isReadyForCalculation && storeItems.length === 0) return [];
    return storeItems.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price
    }));
  }, [storeItems, isReadyForCalculation]);

  const entityKits = useMemo(() => {
    if (!isReadyForCalculation && kits.length === 0) return [];
    return kits.map(kit => ({
      id: kit.id,
      name: kit.name,
      price: kit.price
    }));
  }, [kits, isReadyForCalculation]);

  const selectedInventoryItems = useMemo(() => {
    return formData?.inventoryItems?.map(item => ({
      id: item.id,
      quantity: item.quantity || 1
    })) || [];
  }, [formData?.inventoryItems]);

  // Используем унифицированный хук расчетов (мемоизированные параметры)
  const calculationParams = useMemo(() => ({
    startDate: formData?.startDate,
    endDate: formData?.endDate,
    inventoryItems: entityInventoryItems,
    selectedInventoryItems,
    storeItems: entityStoreItems,
    selectedStoreItems,
    kits: entityKits,
    selectedKitIds: selectedKits,
    discount,
    mode: 'mixed' as const
  }), [
    formData?.startDate,
    formData?.endDate,
    entityInventoryItems,
    selectedInventoryItems,
    entityStoreItems,
    selectedStoreItems,
    entityKits,
    selectedKits,
    discount
  ]);

  const { totalAmount, breakdown } = useEntityCalculations(calculationParams);

  // Обратная совместимость: функция для ручного пересчета (если нужна)
  const calculateTotalAmount = (): number => {
    return totalAmount;
  };

  return {
    totalAmount,
    breakdown, // дополнительная детализация расчетов
    calculateTotalAmount // обратная совместимость
  };
};
